import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Instagram, Facebook } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer" className="bg-forest text-cream">
      {/* Newsletter Section */}
      <div className="border-b border-cream/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-heading text-3xl md:text-4xl mb-4">
                Slow down. Reconnect.
              </h3>
              <p className="text-cream/70 text-lg font-body">
                Sign up for grounding tips, upcoming events, and wellness insights.
              </p>
            </div>
            <form className="flex flex-col sm:flex-row gap-4" data-testid="newsletter-form">
              <input
                type="email"
                placeholder="Your email address"
                data-testid="newsletter-email-input"
                className="flex-1 bg-cream/10 border border-cream/20 rounded-full px-6 py-4 text-cream placeholder:text-cream/50 focus:outline-none focus:border-terracotta"
              />
              <button
                type="submit"
                data-testid="newsletter-submit-btn"
                className="bg-terracotta text-white px-8 py-4 rounded-full font-medium hover:bg-terracotta-dark transition-all duration-300"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <img 
              src="https://customer-assets.emergentagent.com/job_restore-website/artifacts/iujcea5s_New%20logojan%202026.png" 
              alt="Grounded Wellness with Jill"
              className="h-16 w-auto mb-4 brightness-0 invert opacity-90"
            />
            <p className="text-cream/70 font-body text-sm leading-relaxed">
              Reconnecting you to your body, your health, and your whole self through movement, nutrition, and deep inner work.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h5 className="font-body font-semibold text-sm uppercase tracking-widest mb-6 text-terracotta">
              Explore
            </h5>
            <ul className="space-y-3">
              {[
                { name: 'About', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Events', path: '/events' },
                { name: 'Testimonials', path: '/testimonials' },
                { name: 'Blog', path: '/blog' },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-cream/70 hover:text-terracotta transition-colors font-body text-sm"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h5 className="font-body font-semibold text-sm uppercase tracking-widest mb-6 text-terracotta">
              Services
            </h5>
            <ul className="space-y-3">
              {[
                'Restorative Training',
                'Health Coaching',
                'Holistic Coaching',
                'Group Sessions',
                'Private Sessions',
              ].map((item) => (
                <li key={item}>
                  <Link
                    to="/services"
                    className="text-cream/70 hover:text-terracotta transition-colors font-body text-sm"
                  >
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h5 className="font-body font-semibold text-sm uppercase tracking-widest mb-6 text-terracotta">
              Connect
            </h5>
            <ul className="space-y-4">
              <li className="flex items-center gap-3 text-cream/70">
                <Mail size={18} className="text-terracotta" />
                <a href="mailto:heyjbennett@yahoo.ca" className="font-body text-sm hover:text-terracotta transition-colors">heyjbennett@yahoo.ca</a>
              </li>
              <li className="flex items-start gap-3 text-cream/70">
                <MapPin size={18} className="text-terracotta mt-1" />
                <span className="font-body text-sm">Breathing Room<br />9 Par La Ville<br />Hamilton, Bermuda HM11</span>
              </li>
            </ul>
            <div className="flex gap-4 mt-6">
              <a
                href="https://www.instagram.com/groundedwellnesswithjill"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-instagram"
                className="w-10 h-10 rounded-full bg-cream/10 flex items-center justify-center hover:bg-terracotta transition-colors"
              >
                <Instagram size={18} />
              </a>
              <a
                href="https://www.facebook.com/groundedwellnesswithjill"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="social-facebook"
                className="w-10 h-10 rounded-full bg-cream/10 flex items-center justify-center hover:bg-terracotta transition-colors"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="border-t border-cream/10 mt-12 pt-8" data-testid="footer-disclaimer">
          <p className="text-cream/50 text-xs font-body leading-relaxed max-w-4xl">
            <span className="text-cream/70 font-medium">Disclaimer:</span> As a Holistic Health Practitioner and Restorative Personal Trainer, Jill provides supportive holistic approaches for recovering, reversing, and potentially becoming healthier than you ever thought you could be. The information shared on this website is <span className="text-cream/70 font-medium">not medical advice</span> and is not a replacement for medical care. It is offered for your highest wellness — please consult your doctor where appropriate.
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-cream/10 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-cream/50 text-sm font-body">
            © {currentYear} Grounded Wellness with Jill. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className="text-cream/50 hover:text-cream text-sm font-body">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-cream/50 hover:text-cream text-sm font-body">
              Terms & Conditions
            </Link>
            <Link to="/admin" className="text-cream/30 hover:text-cream/50 text-sm font-body">
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
